// src/ui/screens/settings/SettingsScreen.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import UserAvatar from '../../../components/UserAvatar';
import { userProfileService } from '../../../services/user/userProfileService';
import { notificationService } from '../../../services/notificationService';
import { db, auth } from '../../../firebase';
import {
  affiliationService,
  SellerCategoryType,
} from '../../../services/affiliation/affiliationService';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

const APP_STORE_ID = 'com.filatom.app';
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${APP_STORE_ID}`;

// Desktop layout constant — this screen has no category sidebar (unlike
// PostScreen), so content only needs to clear the DesktopNavRail itself.
const DESKTOP_RAIL_WIDTH = 76;

// ── Format phone number ─────────────────────────────────────────────────────

const formatPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('+256') && digits.length === 13) {
    return `${digits.substring(0, 4)} ${digits.substring(4, 7)} ${digits.substring(7, 10)} ${digits.substring(10)}`;
  }
  if (digits.startsWith('+1') && digits.length === 12) {
    return `${digits.substring(0, 2)} ${digits.substring(2, 5)} ${digits.substring(5, 8)} ${digits.substring(8)}`;
  }
  return phone;
};

// ── Toast (lightweight; swap for your app's toast system if you have one) ────

const useToast = () => {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2000);
  }, []);
  const ToastEl = toast ? (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-8 z-50 px-4 py-2.5 rounded-xl text-sm font-medium text-white shadow-lg"
      style={{ backgroundColor: COLORS.card }}
    >
      {toast}
    </div>
  ) : null;
  return [ToastEl, showToast];
};

// ── Notification / count Badge ──────────────────────────────────────────────

const NotificationBadge = ({ count }) => {
  const label = count > 99 ? '99+' : String(count);

  return (
    <div
      className="min-w-[22px] h-[22px] px-1.5 rounded-full flex items-center justify-center transition-all duration-200"
      style={{
        backgroundColor: COLORS.orange,
        boxShadow: `0 2px 6px ${COLORS.orange}59`,
      }}
    >
      <span className="text-black text-[11px] font-extrabold leading-none">
        {label}
      </span>
    </div>
  );
};

// ── Settings Item (generic row) ──────────────────────────────────────────────

const SettingsItem = ({ title, icon, onTap, badge = null }) => {
  const getIconPath = () => {
    switch (icon) {
      case 'verified':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        );
      case 'star':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        );
      case 'headset':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        );
      case 'info':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        );
      case 'notifications':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        );
      case 'speed':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        );
      default:
        return null;
    }
  };

  return (
    <button
      onClick={onTap}
      className="w-full flex items-center gap-3.5 px-3.5 py-3.5 hover:bg-filatom-card-hover transition-colors lg:px-5 lg:py-4"
    >
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {getIconPath()}
      </svg>
      <span className="flex-1 text-left text-white text-sm">{title}</span>
      {badge !== null && badge > 0 && (
        <NotificationBadge count={badge} />
      )}
      <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

// ── ID Verification item — state-aware, mirrors _buildVerificationItem ──────

const VerificationItem = ({ verified, onTap }) => (
  <button
    onClick={onTap}
    className="w-full flex items-center gap-3.5 px-3.5 py-3.5 hover:bg-filatom-card-hover transition-colors lg:px-5 lg:py-4"
  >
    {verified ? (
      <svg className="w-5 h-5" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ) : (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    )}
    <span
      className="flex-1 text-left text-sm"
      style={{ color: verified ? COLORS.orange : 'white', fontWeight: verified ? 600 : 400 }}
    >
      {verified ? 'ID Verified' : 'ID Verification'}
    </span>
    {!verified && (
      <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    )}
  </button>
);

// ── Affiliation item — verification-gated, with pending-count badge ────────

const AffiliationItem = ({ label, badgeCount, onTap }) => (
  <button
    onClick={onTap}
    className="w-full flex items-center gap-3.5 px-3.5 py-3.5 hover:bg-filatom-card-hover transition-colors lg:px-5 lg:py-4"
  >
    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20a6 6 0 0112 0M18 8a2.5 2.5 0 10-3-4M6 8a2.5 2.5 0 013-4M22 20a5 5 0 00-4-4.9M2 20a5 5 0 014-4.9" />
    </svg>
    <span className="flex-1 text-left text-white text-sm">{label}</span>
    {badgeCount > 0 && <NotificationBadge count={badgeCount} />}
    <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

// ── Divider ─────────────────────────────────────────────────────────────────

const SettingsDivider = () => {
  return <div className="h-px bg-white/5 ml-12" />;
};

// ── Settings Screen ─────────────────────────────────────────────────────────

export const SettingsScreen = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  // Verification status (mirrors _isVerifiedSeller / _categoryType)
  const [isVerifiedSeller, setIsVerifiedSeller] = useState(false);
  const [categoryType, setCategoryType] = useState(null);

  // Affiliation badge (pending incoming requests)
  const [pendingAffiliationCount, setPendingAffiliationCount] = useState(0);

  const [ToastEl, showToast] = useToast();

  // ── Load profile ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const cached = userProfileService.cachedProfile;
        if (cached) {
          setProfile(cached);
          setProfileLoading(false);
          return;
        }
        const profileData = await userProfileService.getCurrentUserProfile();
        if (profileData) {
          setProfile(profileData);
        }
      } catch (error) {
        console.error('Load profile error:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, []);

  // ── Subscribe to notification count ──────────────────────────────────────

  useEffect(() => {
    const unsubscribe = notificationService.unreadCountStream((count) => {
      setUnreadNotifCount(count);
    });
    return () => unsubscribe?.();
  }, []);

  // ── Subscribe to verification status ─────────────────────────────────────

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;

    const unsubscribe = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (!snap.exists()) return;
      const verification = snap.data()?.verification;
      setIsVerifiedSeller(verification?.status === 'approved');
      setCategoryType(verification?.categoryType ?? null);
    });

    return () => unsubscribe();
  }, []);

  // ── Subscribe to pending affiliation count ───────────────────────────────

  useEffect(() => {
    const unsubscribe = affiliationService.incomingRequestCountStream((count) => {
      setPendingAffiliationCount(count);
    });
    return () => unsubscribe?.();
  }, []);

  const isBusinessType = categoryType === SellerCategoryType.business;
  const affiliationLabel = isBusinessType ? 'Affiliated profiles' : 'Affiliated companies';

  // ── Rate App ──────────────────────────────────────────────────────────────
  //
  // `market://` is a custom URI scheme that only Android's Play Store app
  // registers a handler for. It can only be resolved by *navigating* the
  // current tab/webview to it (window.location.href) — it is NOT something
  // window.open() can hand off, since window.open just opens a normal blank
  // browsing-context tab when the target scheme has no web handler, which is
  // exactly the empty tab you were seeing. It also never reports success or
  // failure, so a fire-and-forget setTimeout fallback always fires too,
  // opening a second tab regardless of whether the hand-off worked.
  //
  // Fix: only attempt the market:// hand-off on Android (iOS/desktop have no
  // handler for it at all), navigate the current tab to it, and use the
  // page's visibility as the signal for whether it worked — if Android
  // suspends this tab to open the Play Store app, `document.hidden` becomes
  // true and we skip the fallback; otherwise we open the Play Store web page.

  const handleRateApp = () => {
    const isAndroid = /android/i.test(navigator.userAgent || '');

    if (!isAndroid) {
      // No market:// handler exists on iOS/desktop — go straight to the web page.
      window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
      return;
    }

    let handedOff = false;
    const onVisibilityChange = () => {
      if (document.hidden) handedOff = true;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    try {
      window.location.href = `market://details?id=${APP_STORE_ID}`;
    } catch (error) {
      console.error('Rate app error:', error);
    }

    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (!handedOff) {
        window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
      } else {
        showToast('Thank you for your feedback!');
      }
    }, 800);
  };

  // ── Navigate to verification ─────────────────────────────────────────────
  // App.jsx registers this at "/verifications/id", not "/id-verification"

  const handleNavigateToVerification = () => {
    navigate('/verifications/id');
  };

  // ── ID Verification tap — gated the same way as the Flutter build ───────

  const handleVerificationTap = () => {
    if (isVerifiedSeller) {
      showToast("You're already verified");
      return;
    }
    handleNavigateToVerification();
  };

  // ── Navigate to affiliation ───────────────────────────────────────────────
  // App.jsx registers this at "/settings/affiliation"

  const handleAffiliationTap = () => {
    if (!isVerifiedSeller) {
      showToast('Verify your identity to affiliate to companies');
      return;
    }
    navigate('/settings/affiliation');
  };

  // ── Navigate to optimize app ─────────────────────────────────────────────
  // App.jsx registers this at "/settings/optimize-app"

  const handleNavigateToOptimizeApp = () => {
    navigate('/settings/optimize-app');
  };

  // ── Navigate to notifications ────────────────────────────────────────────
  // App.jsx registers this at "/notifications" — already correct

  const handleNavigateToNotifications = () => {
    navigate('/notifications');
  };

  // ── Navigate to contact us ───────────────────────────────────────────────
  // App.jsx registers this at "/settings/contact-us", not "/contact-us"

  const handleNavigateToContactUs = () => {
    navigate('/settings/contact-us');
  };

  // ── Navigate to about ────────────────────────────────────────────────────
  // App.jsx registers this at "/settings/about", not "/about"

  const handleNavigateToAbout = () => {
    navigate('/settings/about');
  };

  // ── Navigate to profile ──────────────────────────────────────────────────

  const handleNavigateToProfile = () => {
    navigate('/profile');
  };

  // ── Navigate to QR display ───────────────────────────────────────────────
  // App.jsx registers this at "/settings/qr", not "/qr-display"

  const handleNavigateToQR = () => {
    navigate('/settings/qr');
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg lg:pl-[76px]">
      {/* Top Bar.
          Mobile: unchanged — spacer + centered title + QR button.
          Desktop: bar shifts past the DesktopNavRail (`lg:left-[76px]`),
          the centering spacer is dropped (`lg:hidden`) so "Settings" reads
          as a normal left-aligned page title next to the QR action on the
          right — the standard settings-page header pattern instead of a
          mobile app-bar stretched full width. */}
      <div
        className="fixed top-0 left-0 right-0 z-10 pt-safe-top px-4 bg-filatom-bg/95 lg:left-[76px] lg:px-10"
        style={{ paddingTop: 12 }}
      >
        <div className="flex items-center justify-between h-11 lg:h-20">
          <div className="w-11 lg:hidden" />
          <span className="text-white text-xl font-bold lg:text-2xl">Settings</span>
          <button
            onClick={handleNavigateToQR}
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: 'rgba(35,38,44,0.3)',
              backdropFilter: 'blur(24px)',
              border: '1px solid #23262C',
            }}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content.
          Mobile: unchanged — full-width, pt-[72px]/px-4 exactly as before.
          Desktop: becomes a centered, max-width column (`lg:max-w-2xl
          lg:mx-auto`) with generous top/side spacing (`lg:pt-28 lg:px-10`)
          instead of the mobile card stretched edge-to-edge next to the nav
          rail — the standard narrow, readable settings-page layout. */}
      <div className="pt-[72px] px-4 pb-32 lg:pt-28 lg:px-10 lg:pb-20 lg:max-w-2xl lg:mx-auto">
        {/* Profile Tile */}
        <button
          onClick={handleNavigateToProfile}
          className="w-full p-3.5 rounded-xl flex items-center gap-3.5 lg:p-5 lg:border lg:border-white/5 lg:hover:border-white/10 transition-colors"
          style={{ backgroundColor: COLORS.card }}
        >
          {profileLoading ? (
            <div className="w-[50px] h-[50px] rounded-full bg-filatom-cardLight animate-pulse" />
          ) : (
            <UserAvatar
              size={50}
              imageUrl={profile?.hasProfileImage ? profile.profileImage : null}
              name={profile?.displayName}
            />
          )}
          <div className="flex-1 text-left">
            <p className="text-white text-lg font-semibold">
              {profileLoading ? '…' : (profile?.displayName || 'User')}
            </p>
            <p className="text-filatom-primary text-sm">
              {profileLoading ? '' : formatPhone(profile?.phone || '')}
            </p>
          </div>
          <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Add Account - Disabled */}
        <div
          className="mt-3.5 p-3.5 rounded-xl flex items-center gap-3.5 lg:p-5 lg:border lg:border-white/5"
          style={{ backgroundColor: `${COLORS.card}80` }}
        >
          <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="flex-1 text-white/40 text-sm">Add new account</span>
          <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </div>

        {/* Main Settings Card */}
        <div className="mt-4 rounded-xl overflow-hidden lg:border lg:border-white/5" style={{ backgroundColor: COLORS.card }}>
          {/* ID Verification — state-aware */}
          <VerificationItem verified={isVerifiedSeller} onTap={handleVerificationTap} />
          <SettingsDivider />

          {/* Notifications — live badge */}
          <SettingsItem
            title="Notifications"
            icon="notifications"
            onTap={handleNavigateToNotifications}
            badge={unreadNotifCount}
          />
          <SettingsDivider />

          <SettingsItem
            title="Rate App on Store"
            icon="star"
            onTap={handleRateApp}
          />
          <SettingsDivider />

          <SettingsItem
            title="Contact Us"
            icon="headset"
            onTap={handleNavigateToContactUs}
          />
          <SettingsDivider />

          <SettingsItem
            title="About"
            icon="info"
            onTap={handleNavigateToAbout}
          />
          <SettingsDivider />

          {/* Optimize App */}
          <SettingsItem
            title="Optimize App"
            icon="speed"
            onTap={handleNavigateToOptimizeApp}
          />
          <SettingsDivider />

          {/* Affiliated companies / Affiliated profiles — verification-gated */}
          <AffiliationItem
            label={affiliationLabel}
            badgeCount={isVerifiedSeller ? pendingAffiliationCount : 0}
            onTap={handleAffiliationTap}
          />
        </div>
      </div>

      {ToastEl}
    </div>
  );
};

export default SettingsScreen;